/**
 * Lectura de `GET /getunitcampaigns` (sección «Campañas» de la ficha, E4).
 * Funciones puras. Ver docs/maps/meta-ads.md § Campañas en la ficha.
 */
import { formatMetaMoney, readMetaLinks, readMetaTotals } from './metaAdsMoney';
import { metaEffectiveStatusLabel, readMetaLinkedBy } from './metaCampaigns';
import {
  META_DEFAULT_PERIOD_PRESET, META_PERIOD_PRESETS, formatMetaDateTime, formatMetaPeriod, metaPeriodPresetRange,
} from './metaPeriod';
import { readMetaAdCreative } from './metaAdPreview';
import { stripMetaTerrioMark } from './metaFormat';
import { readMetaFase1Signal } from './metaFase1Support';
import { readMetaDelivery, readMetaRecommendations } from './metaDelivery';
import { formatMetaContacts, formatMetaContactsShort, metaContactsNote, metaContactsShortNote } from './metaContacts';

const str = (value) => (typeof value === 'string' && value.trim() ? value.trim() : null);
const num = (value) => (value == null || value === '' ? null : (Number.isFinite(Number(value)) ? Number(value) : null));
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** «esta unidad» / «este lote» según el tipo de registro. */
export const metaRecordNoun = (itemKind) => (itemKind === 'user_lot'
  ? { this: 'esta unidad', the: 'la unidad' }
  : { this: 'este lote', the: 'el lote' });

function deliveryFields(raw, effectiveStatus) {
  const delivery = readMetaDelivery(raw, metaEffectiveStatusLabel(effectiveStatus), effectiveStatus);
  return { statusLabel: delivery.label, delivery };
}

function normalizeUnitAd(raw) {
  const id = str(raw?.ad_id == null ? '' : String(raw.ad_id));
  if (!id) return null;
  const { metaUrl } = readMetaLinks(raw);
  const effectiveStatus = str(raw?.effective_status) || str(raw?.status);
  const sharedCount = Math.max(1, Math.trunc(num(raw?.shared_count) || 1));
  return {
    id,
    name: str(stripMetaTerrioMark(raw?.ad_name)) || `Anuncio ${id}`,
    campaignId: str(raw?.campaign_id == null ? '' : String(raw.campaign_id)),
    campaignName: str(stripMetaTerrioMark(raw?.campaign_name)),
    currency: str(raw?.currency)?.toUpperCase() || null,
    metaUrl,
    // Ajuste 6: `delivery_label` manda; sin él, la etiqueta de `effective_status`.
    ...deliveryFields(raw, effectiveStatus),
    recommendations: readMetaRecommendations(raw),
    linkedVia: raw?.linked_via === 'campaign' ? 'campaign' : 'ad',
    // Quién / cuándo vinculó (API aprobada, opcional: sin ellos no se muestra nada).
    linkedBy: readMetaLinkedBy(raw),
    linkedByUserId: str(raw?.linked_by_user_id),
    linkedAt: str(raw?.linked_at),
    isShared: raw?.is_shared === true || sharedCount > 1,
    sharedCount,
    sharedLabel: str(raw?.shared_label) || (sharedCount > 1 ? `Anuncio compartido con ${sharedCount} unidades` : null),
    metrics: raw?.metrics && typeof raw.metrics === 'object' ? raw.metrics : null,
    // Ajuste 12: miniatura (solo https) / video, como en el panel.
    ...readMetaAdCreative(raw),
  };
}

/**
 * Tipo del anuncio (Ajuste B): `creative_type` es el `object_type` crudo de Meta o `null`.
 * VIDEO → Video, PHOTO → Imagen, SHARE → «Enlace / carrusel»; otro valor o null → «Anuncio».
 */
const CREATIVE_TYPE_LABELS = {
  VIDEO: 'Video',
  PHOTO: 'Imagen',
  SHARE: 'Enlace / carrusel',
};
export const META_CREATIVE_FALLBACK_LABEL = 'Anuncio';

export function metaCreativeTypeLabel(creativeType) {
  return CREATIVE_TYPE_LABELS[String(creativeType || '').trim().toUpperCase()] || META_CREATIVE_FALLBACK_LABEL;
}

/**
 * Sugerido de `unlinked_new_ads`. `currency`, `status`, `effective_status`,
 * `creative_type` y `first_seen_at` son opcionales (API aprobada, sin métricas):
 * sin ellos queda como antes (nombre, campaña, «Ver en Meta»).
 */
function normalizeNewAd(raw) {
  const id = str(raw?.ad_id == null ? '' : String(raw.ad_id));
  if (!id) return null;
  const effectiveStatus = str(raw?.effective_status) || str(raw?.status);
  return {
    id,
    name: str(stripMetaTerrioMark(raw?.ad_name)) || `Anuncio ${id}`,
    campaignId: str(raw?.campaign_id == null ? '' : String(raw.campaign_id)),
    campaignName: str(stripMetaTerrioMark(raw?.campaign_name)),
    metaUrl: readMetaLinks(raw).metaUrl,
    currency: str(raw?.currency)?.toUpperCase() || null,
    effectiveStatus,
    ...deliveryFields(raw, effectiveStatus),
    creativeType: str(raw?.creative_type)?.toUpperCase() || null,
    // Solo si la API manda el campo (Ajuste 4): con la API vieja no se inventa «Anuncio».
    creativeLabel: 'creative_type' in (raw || {}) ? metaCreativeTypeLabel(raw.creative_type) : null,
    firstSeenAt: str(raw?.first_seen_at),
  };
}

/**
 * «Activo · Video · Apareció el 21 oct 06:00» de un sugerido (Ajuste 4; hora de
 * Córdoba). Vacío si la API no manda estado, tipo ni `first_seen_at`.
 */
export function describeNewAdMeta(ad, now = new Date()) {
  const seen = formatMetaDateTime(ad?.firstSeenAt, now);
  return [ad?.statusLabel, ad?.creativeLabel, seen ? `Apareció el ${seen}` : null].filter(Boolean).join(' · ');
}

const dedupe = (list) => {
  const seen = new Set();
  return list.filter((item) => {
    if (!item || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

/**
 * @returns {{ period: {since: string, until: string}|null, totals: ReturnType<typeof readMetaTotals>,
 *   ads: Array, unlinkedNewAds: Array, note: string|null }}
 */
export function readUnitCampaigns(res) {
  const data = res?.data && typeof res.data === 'object' ? res.data : {};
  const ads = dedupe((Array.isArray(data.ads) ? data.ads : []).map(normalizeUnitAd));
  const linked = new Set(ads.map((a) => a.id));
  return {
    period: data.period && data.period.since ? { since: data.period.since, until: data.period.until } : null,
    // Nombre del registro (`record.label`) para el selector «Vincular campaña»; null si no viene.
    recordLabel: str(data.record?.label),
    totals: readMetaTotals('unit', data),
    ads,
    // Un anuncio ya vinculado nunca se sugiere (defensa).
    unlinkedNewAds: dedupe((Array.isArray(data.unlinked_new_ads) ? data.unlinked_new_ads : []).map(normalizeNewAd))
      .filter((a) => !linked.has(a.id)),
    note: str(data.totals_note),
    // `true` estricto = la API de este entorno tiene la fase 1 (informe incluido); ausente = API sin fase 1.
    budgetAvailable: readMetaFase1Signal(data),
  };
}

/**
 * «ARS 60.000 · 28 conversaciones + 2 formularios · ARS 2.000 por contacto» de un bloque
 * `metrics` en una moneda (sin desglose: «30 contactos», como antes).
 * `short` (ficha con fase 1): contactos como total («30 contactos»), para que entre en una
 * línea; el desglose va en el ⓘ / Tooltip (`metaContactsShortNote`). Mismos valores.
 */
export function metaMetricsSummary(metrics, currency, { withMoney = true, short = false } = {}) {
  if (!metrics) return '';
  const parts = [];
  const spend = num(metrics.spend);
  if (withMoney && spend != null && currency) parts.push(formatMetaMoney(spend, currency));
  const contacts = short ? formatMetaContactsShort(metrics) : formatMetaContacts(metrics);
  if (contacts) parts.push(contacts);
  const cpc = num(metrics.cost_per_contact);
  if (withMoney && cpc != null && currency) parts.push(`${formatMetaMoney(cpc, currency)} por contacto`);
  return parts.join(' · ');
}

/**
 * Filas de totales para mostrar. Una moneda: una fila con plata. Mezcla: una
 * fila por moneda (de `totals_by_currency`) y nunca se suman montos; los
 * contactos totales sí (vienen sumados en `totals`).
 *
 * `note` / `contactsNote`: «N contactos en total» para el Tooltip (o `null` sin desglose).
 *
 * @returns {{ mixed: boolean, rows: Array<{ currency: string|null, text: string, note: string|null }>,
 *   contactsText: string, contactsNote: string|null }}
 */
export function unitTotalsRows(totals, { short = false } = {}) {
  if (!totals) return { mixed: false, rows: [], contactsText: '', contactsNote: null };
  // `short`: mismo texto corto que `metaMetricsSummary` y la nota con el desglose.
  const noteOf = short ? metaContactsShortNote : metaContactsNote;
  if (totals.mixed) {
    return {
      mixed: true,
      rows: totals.byCurrency
        .filter((r) => r.currency)
        .map((r) => ({ currency: r.currency, text: metaMetricsSummary(r.metrics, r.currency, { short }), note: noteOf(r.metrics) })),
      contactsText: metaMetricsSummary(totals.total, null, { withMoney: false, short }),
      contactsNote: noteOf(totals.total),
    };
  }
  const text = metaMetricsSummary(totals.total, totals.currency, { short });
  return {
    mixed: false,
    rows: text ? [{ currency: totals.currency, text, note: noteOf(totals.total) }] : [],
    contactsText: '',
    contactsNote: null,
  };
}

/** Subtítulo de la sección: «2 anuncios · ARS 60.000» (mezcla: «ARS 60.000 · USD 120»). */
export function describeUnitCampaignsSummary(state) {
  if (!state) return '';
  const n = state.ads.length;
  if (!n) return 'Sin anuncios vinculados';
  const t = state.totals;
  let money = '';
  if (t?.mixed) {
    money = t.byCurrency
      .filter((r) => r.currency && num(r.metrics?.spend) != null)
      .map((r) => formatMetaMoney(r.metrics.spend, r.currency))
      .join(' · ');
  } else if (num(t?.total?.spend) != null && t.currency) {
    money = formatMetaMoney(t.total.spend, t.currency);
  }
  return [plural(n, 'anuncio', 'anuncios'), money].filter(Boolean).join(' · ');
}

/**
 * Aviso de la última sincronización (`getmetaconnection`): `error` / `partial`
 * o conexión cortada.
 *
 * @returns {{ tone: 'warning'|'info', title: string }|null}
 */
export function unitSyncNotice(connection) {
  if (!connection) return null;
  if (connection.needsReconnect) return { tone: 'warning', title: 'Se cortó la conexión con Meta' };
  if (connection.lastSyncStatus === 'error') return { tone: 'warning', title: 'La última actualización con Meta falló' };
  // Ajuste 7: `partial` ya no sale por tiempo (sin `time_budget_exhausted` / `cut_by_time`).
  if (connection.lastSyncStatus === 'partial') return { tone: 'warning', title: 'La última actualización con Meta quedó incompleta' };
  return null;
}

/**
 * ¿Hubo una conexión antes? Desconectar **sin** `purge_data` deja `status:
 * "not_connected"` pero conserva `last_sync_at` (Ajuste A: los contadores, incluido
 * `tracked_count`, quedan en 0 aunque haya historial). Si hay historial o no lo deciden
 * los datos de getunitcampaigns.
 */
export const hadMetaConnection = (connection) => Boolean(connection?.lastSyncAt);

/**
 * Qué hace la sección «Campañas» según la conexión del espacio:
 * - `reconnect`: `needs_reconnect` (expired / error) → pide igual (getunitcampaigns no mira la
 *   conexión); con anuncios, historial en solo lectura con «Reconectá Meta»; vacío → la línea
 *   «Reconectá Meta…» (dueño) o nada (editor).
 * - `ready`: conectado y con cuentas seguidas → pide y muestra todo.
 * - `history`: desconectado sin purge → pide igual; con anuncios muestra el
 *   historial en solo lectura (aviso + «Reconectar» del dueño); vacío → como `connect`.
 * - `connect`: dueño sin conexión / sin cuentas → la línea «Conectá Meta…», sin request.
 * - `none`: editor sin conexión → nada.
 *
 * @returns {'ready'|'reconnect'|'history'|'connect'|'none'}
 */
export function unitCampaignsAccess(connection) {
  if (!connection) return 'none';
  if (connection.needsReconnect || connection.status === 'expired' || connection.status === 'error') return 'reconnect';
  if (connection.connected && connection.adAccounts?.trackedCount > 0) return 'ready';
  if (connection.status === 'not_connected' && hadMetaConnection(connection)) return 'history';
  return connection.viewerCanManage ? 'connect' : 'none';
}

/**
 * Período del bloque «Campañas» de la ficha: el mismo preset por defecto que el panel
 * («Últimos 30 días» = hoy−30 a ayer, hora de Córdoba) para que los números coincidan.
 * Va a `getunitcampaigns` como `since` / `until` (`buildMetaPeriodParams`).
 */
export const UNIT_CAMPAIGNS_PERIOD_PRESET = META_DEFAULT_PERIOD_PRESET;

export function unitCampaignsPeriod(now = new Date()) {
  return metaPeriodPresetRange(UNIT_CAMPAIGNS_PERIOD_PRESET, now);
}

/**
 * «Últimos 30 días · 26 sep – 25 oct» si la API respondió el período pedido; si no, solo
 * el rango de la respuesta. «(hoy, parcial)» solo cuando el rango termina hoy.
 */
export function describeUnitCampaignsPeriod(period, requested = null, now = new Date()) {
  const range = formatMetaPeriod(period, now);
  if (!range) return '';
  const same = requested && period?.since === requested.since && period?.until === requested.until;
  const label = same ? META_PERIOD_PRESETS.find((p) => p.key === UNIT_CAMPAIGNS_PERIOD_PRESET)?.label : null;
  return label ? `${label} · ${range}` : range;
}

/**
 * Anuncios de la ficha agrupados por campaña (se lee primero la campaña), en el orden de
 * la API. Sin `campaign_id`, cada anuncio va solo.
 *
 * @returns {Array<{ key: string, campaignId: string|null, campaignName: string|null, ads: Array }>}
 */
export function groupUnitAdsByCampaign(ads) {
  const groups = [];
  const byId = new Map();
  (Array.isArray(ads) ? ads : []).forEach((ad) => {
    if (!ad) return;
    const key = ad.campaignId ? `campaign:${ad.campaignId}` : `ad:${ad.id}`;
    let group = byId.get(key);
    if (!group) {
      group = { key, campaignId: ad.campaignId || null, campaignName: ad.campaignName || null, ads: [] };
      byId.set(key, group);
      groups.push(group);
    }
    if (!group.campaignName && ad.campaignName) group.campaignName = ad.campaignName;
    group.ads.push(ad);
  });
  return groups;
}
